import { PropsWithChildren, ReactElement, ReactNode, useCallback, useEffect } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { chatCommunicationController } from '../../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import { Logger } from '../../../logger/Logger';
import { useNickNameFetcher } from '../../../store/apiHooks/useNickNameFetcher';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { nickNameInfoSlice } from '../../../store/slices/nickname/selectors';
import { useMasterVolumeControl } from '../../../store/slices/settings/hooks/useVolumeControl';
import { useSettingsSlice } from '../../../store/slices/settings/selectors';
import { settingsActions } from '../../../store/slices/settings/slice';
import { StreamQuality } from '../../../store/slices/settings/types';
import { useSubtitlesSlice } from '../../../store/slices/subtitles/selectors';
import { subtitlesActions } from '../../../store/slices/subtitles/slice';
import { SubtitlesGroup } from '../../../store/slices/subtitles/types';
import { ControlContainer } from '../../controls/controlContainer';
import { RadioGroup } from '../../controls/radioGroup/RadioGroup';
import { Toggle } from '../../controls/toggle/Toggle';
import { VolumeSlider } from '../../controls/volumeSlider';
import { ErrorBoundary } from '../../errorBoundary/ErrorBoundary';
import { Icons } from '../../icons/iconNames';
import { Loader } from '../../loader';
import { ModalContainer } from '../../modal/modalContainer';
import { ModalSection } from '../../modal/modalSection';
import { SubtitlesPreview } from '../../subtitles/preview/SubtitlesPreview';
import { SubtitlesSize } from '../../subtitles/types';
import { TabProps, Tabs } from '../../tabs/Tabs';
import { L12nText } from '../../text/L12nText';
import { NickNameSettingsConnected } from '../nickName/NickNameSettingsConnected';
import styles from '../SettingsModal.module.css';
import { VolumeChannelControlConnected } from '../VolumeChannelControlConnected';

interface SettingsWrapperProps extends PropsWithChildren {
  gameSpecificTab?: TabProps[];
}

const SettingsWrapper = ({ gameSpecificTab, children }: SettingsWrapperProps): ReactNode => {
  const generalTab: TabProps = {
    label: <L12nText tk="GENERAL" />,
    content: children,
  };

  return gameSpecificTab ? (
    <Tabs
      className={styles.tabs}
      classNameContainer={styles.tab_container}
      isOneWidth
      tabs={[generalTab, ...gameSpecificTab]}
    />
  ) : (
    <div className={styles.settingsPageSingle}>{children}</div>
  );
};

export const SettingsConnected = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const { soundControls, gameSettingsSpecificTab } = useGameConfig();

  const isStreamEnabled = useSettingsSlice.getIsStreamEnabled();
  const streamQuality = useSettingsSlice.getStreamQuality();
  const streamQualityLimit = useSettingsSlice.getStreamQualityLimit();
  const masterVolumeControl = useMasterVolumeControl();
  const noVolumeControl = useSettingsSlice.getNoVolumeControl();
  const showOtherMessages = useSettingsSlice.getShowOtherMessages();
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const userId = useConfigSlice.userId();
  const isSubtitlesAvailable = useConfigSlice.isSubtitlesAvailable();
  const isSubtitlesEnabled = useSubtitlesSlice.getIsEnabled();
  const subtitlesSize = useSubtitlesSlice.getSize();
  const subtitlesGroup = useSubtitlesSlice.getGroup();
  const subtitlesSectionTitleKey =
    subtitlesGroup === SubtitlesGroup.GROUP3 ? 'SETTINGS_SUBTITLES_ENGLISH' : 'SETTINGS_SUBTITLES';
  const { isNickNameInfoFetched } = nickNameInfoSlice.getNickNameInfo();
  const nickNameFetcher = useNickNameFetcher();

  const handleStreamToggle = (checked: boolean): void => {
    Logger.logEvent('click', 'settingsDisableStream', Date.now(), 1, '', false);
    dispatch(settingsActions.setStreamEnabled(checked));
  };

  const handleChatToggle = (checked: boolean): void => {
    dispatch(settingsActions.setShowOtherMessages(checked));
    const params = {
      action: 'SETTINGS',
      messageId: `${userId}_${Date.now()}`,
      userId,
      preference: {
        showOtherMessages: checked,
      },
    };
    chatCommunicationController.sendMessage(JSON.stringify(params));
  };

  const handleSubtitlesToggle = (checked: boolean): void => {
    dispatch(subtitlesActions.setIsEnabled(checked));
    const params = {
      action: 'SETTINGS',
      messageId: `${userId}_${Date.now()}`,
      userId,
      preference: {
        subEnabled: checked,
      },
    };
    chatCommunicationController.sendMessage(JSON.stringify(params));
  };

  const isOptionDisabled = useCallback(
    (option: number): boolean => {
      return option < streamQualityLimit;
    },
    [streamQualityLimit]
  );

  useEffect(() => {
    if (streamQuality !== StreamQuality.Auto && isOptionDisabled(streamQuality)) {
      dispatch(settingsActions.setStreamQuality(StreamQuality.Auto));
    }
  }, [dispatch, isOptionDisabled, streamQuality]);

  useEffect(() => {
    !isNickNameInfoFetched && nickNameFetcher();
  }, [isNickNameInfoFetched, nickNameFetcher]);

  return (
    <ErrorBoundary fallback={<Loader />}>
      <SettingsWrapper gameSpecificTab={gameSettingsSpecificTab}>
        <ModalContainer>
          <ModalSection
            title={<L12nText tk="VIDEO" />}
            icon={Icons.Video}
            action={
              <Toggle checked={isStreamEnabled} onChange={(event): void => handleStreamToggle(event.target.checked)} />
            }
          >
            <ControlContainer label={<L12nText tk="VIDEO_QUALITY" />} column offset>
              <RadioGroup
                disabled={!isStreamEnabled}
                name="settings-video-quality"
                options={[
                  /*
                  {
                    label: <L12nText tk="AUTO" />,
                    value: StreamQuality.Auto.toString(),
                  },
                  {
                    disabled: isOptionDisabled(StreamQuality.Low),
                    label: <L12nText tk="LOW" />,
                    tooltip: {
                      disabled: !isOptionDisabled(StreamQuality.Low) || !isStreamEnabled,
                      text: 'AUTO_IS_RECOMMENDED',
                    },
                    value: StreamQuality.Low.toString(),
                  },
                  {
                    disabled: isOptionDisabled(StreamQuality.Medium),
                    label: <L12nText tk="MEDIUM" />,
                    tooltip: {
                      disabled: !isOptionDisabled(StreamQuality.Medium) || !isStreamEnabled,
                      text: 'AUTO_IS_RECOMMENDED',
                    },
                    value: StreamQuality.Medium.toString(),
                  },
                  {
                    disabled: isOptionDisabled(StreamQuality.High),
                    label: <L12nText tk="HIGH" />,
                    tooltip: {
                      disabled: !isOptionDisabled(StreamQuality.High) || !isStreamEnabled,
                      text: 'AUTO_IS_RECOMMENDED',
                    },
                    value: StreamQuality.High.toString(),
                  },
                  */
                  // TODO: uncomment options above and delete options below when video settings CR is approved (https://pragmaticplay.atlassian.net/browse/LCE-71231)
                  {
                    label: <L12nText tk="HIGH" />,
                    value: StreamQuality.High.toString(),
                  },
                  {
                    label: <L12nText tk="MEDIUM" />,
                    value: StreamQuality.Medium.toString(),
                  },
                  {
                    label: <L12nText tk="LOW" />,
                    value: StreamQuality.Low.toString(),
                  },
                  {
                    label: <L12nText tk="AUTO" />,
                    value: StreamQuality.Auto.toString(),
                  },
                ]}
                onChange={(value): void => {
                  Logger.logEvent('click', 'settingsChangeVideo', Date.now(), 1, value, false);
                  dispatch(settingsActions.setStreamQuality(value as StreamQuality));
                }}
                value={streamQuality.toString()}
              />
            </ControlContainer>
          </ModalSection>
          <ModalSection
            title={<L12nText tk="SOUNDS" />}
            icon={Icons.SoundOn}
            action={masterVolumeControl && !noVolumeControl ? <VolumeSlider control={masterVolumeControl} /> : null}
          >
            {soundControls.map((control) => (
              <VolumeChannelControlConnected
                channel={control.channel}
                disabledWithStream={control.disabledWithStream}
                key={control.channel}
                label={control.label}
              />
            ))}
          </ModalSection>
          {!isChatDisabled && (
            <ModalSection
              title={<L12nText tk="CHAT" />}
              icon={Icons.Chat}
              action={
                <Toggle
                  checked={showOtherMessages}
                  onChange={(event): void => handleChatToggle(event.target.checked)}
                />
              }
            >
              <ControlContainer label={<L12nText tk="SETTINGS_SHOW_OTHER_PLAYERS_MESSAGES" />} column offset />
            </ModalSection>
          )}
          {isSubtitlesAvailable && (
            <ModalSection
              title={<L12nText tk={subtitlesSectionTitleKey} />}
              icon={Icons.Subtitles}
              action={
                <Toggle
                  checked={isSubtitlesEnabled}
                  onChange={(event): void => handleSubtitlesToggle(event.target.checked)}
                />
              }
            >
              <ControlContainer label={<L12nText tk="SETTINGS_SUBTITLES_SIZE" />} column offset>
                <RadioGroup
                  className={styles.settings__subtitlesSize}
                  disabled={!isSubtitlesEnabled}
                  name="settings-subtitles-size"
                  options={[
                    {
                      label: <L12nText tk="SETTINGS_SUB_SMALL" />,
                      value: SubtitlesSize.SMALL.toString(),
                    },
                    {
                      label: <L12nText tk="SETTINGS_SUB_MEDIUM" />,
                      value: SubtitlesSize.MEDIUM.toString(),
                    },
                    {
                      label: <L12nText tk="SETTINGS_SUB_LARGE" />,
                      value: SubtitlesSize.LARGE.toString(),
                    },
                  ]}
                  onChange={(value): void => {
                    dispatch(subtitlesActions.setSize(value as SubtitlesSize));
                  }}
                  value={subtitlesSize.toString()}
                />
                <SubtitlesPreview size={subtitlesSize} subtitle={<L12nText tk="SETTINGS_SUB_PREVIEW" />} />
              </ControlContainer>
            </ModalSection>
          )}
          <ModalSection title={<L12nText tk="SETTINGS_ACCOUNT" />} icon={Icons.Player}>
            {/*TODO: Make a plan component version for Storybook testing*/}
            <NickNameSettingsConnected isNickNameInfoFetched={isNickNameInfoFetched} />
          </ModalSection>
        </ModalContainer>
      </SettingsWrapper>
    </ErrorBoundary>
  );
};
